import type { ConversationView } from '@hilo/contracts';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { isUnavailable } from '../../lib/api';
import { CHANNEL_LABEL, KIND_LABEL, STATE_LABEL } from '../../lib/copy';
import { useCurrentUser } from '../../lib/session';
import { formatListDate } from '../../lib/time';
import { Avatar, type AvatarThread } from '../../ui/Avatar';
import { StatusTicks } from '../../ui/StatusTicks';
import { contactName, isUnnamed } from '../conversation/names';
import styles from './ConversationList.module.css';
import { useInboxList } from './useInboxList';

function threadOf(conversation: ConversationView, userId: string): AvatarThread {
  if (conversation.state === 'RESOLVED') return 'none';
  if (conversation.assigneeId === null) return 'free';
  return conversation.assigneeId === userId ? 'mine' : 'other';
}

/** «Tuya» ya la dice el aro firme del avatar; «Libre» y el nombre de otra persona se escriben. */
function assignmentText(conversation: ConversationView, userId: string): string {
  if (conversation.assigneeId === null) return 'Libre';
  if (conversation.assigneeId === userId) return 'Tuya';
  return conversation.assigneeDisplayName ?? 'Asignada';
}

function previewText(conversation: ConversationView): string {
  const last = conversation.lastMessage;
  if (!last) return 'Sin mensajes todavía';
  const body = last.preview ?? KIND_LABEL[last.kind];
  return last.direction === 'OUTBOUND' ? `Tú: ${body}` : body;
}

function Skeleton() {
  return (
    <ul className={styles.list} aria-hidden="true">
      {[0, 1, 2, 3].map((index) => (
        <li key={index} className={styles.skeleton}>
          <span className={styles.boneAvatar} />
          <span className={styles.boneLines}><span /><span /></span>
        </li>
      ))}
    </ul>
  );
}

export function ConversationList() {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const { id: selectedId } = useParams();
  const [searchParams] = useSearchParams();
  const { list, items, filters } = useInboxList();
  const offline = list.isError && isUnavailable(list.error);

  return (
    <div className={styles.wrap}>
      {offline && (
        <p className={styles.offline} role="status">
          Hilo no responde (sin conexión o reiniciándose). Se reintenta cada 10 segundos; lo que ves puede estar desactualizado.
        </p>
      )}
      {list.isPending && <><p className="visually-hidden" role="status">Cargando conversaciones…</p><Skeleton /></>}
      {list.isError && !offline && !list.data && (
        <p className={styles.empty} role="alert">No se pudo cargar la bandeja. Se volverá a intentar en unos segundos.</p>
      )}
      {list.isSuccess && items.length === 0 && (
        <div className={styles.empty}>
          <p className={styles.emptyTitle}>{filters.q ? 'Sin resultados' : 'Nada por aquí'}</p>
          <p>{filters.q ? `Ningún contacto se llama «${filters.q}» con estos filtros.` : 'No hay conversaciones con estos filtros.'}</p>
        </div>
      )}
      <ul className={styles.list} aria-label="Conversaciones">
        {items.map((conversation) => {
          const name = contactName(conversation);
          const thread = threadOf(conversation, user.id);
          const waiting = conversation.lastMessage?.direction === 'INBOUND' && conversation.state !== 'RESOLVED';
          const detailId = `conv-${conversation.id}-detalle`;
          return (
            <li key={conversation.id}>
              <button type="button" className={styles.item} data-resolved={conversation.state === 'RESOLVED' || undefined}
                aria-label={`Abrir conversación de ${name}`} aria-describedby={detailId}
                aria-current={conversation.id === selectedId ? 'true' : undefined}
                onClick={() => void navigate({ pathname: `/conversaciones/${conversation.id}`, search: searchParams.toString() })}>
                <Avatar photoUrl={conversation.contactPhotoUrl} name={conversation.contactDisplayName} seed={conversation.contactId} thread={thread} channel={conversation.channel} />
                <span className={styles.body}>
                  <span className={styles.top}>
                    <span className={styles.name} data-unnamed={isUnnamed(conversation) || undefined} title={name}>{name}</span>
                    <time className={styles.time} data-waiting={waiting || undefined} dateTime={conversation.lastActivityAt}>
                      {formatListDate(conversation.lastActivityAt, user.timezone)}
                    </time>
                  </span>
                  <span id={detailId} className={styles.detail}>
                    <span className={styles.previewRow}>
                      <span className={styles.preview} data-waiting={waiting || undefined}>
                        {conversation.lastMessage?.direction === 'OUTBOUND' && <StatusTicks status={conversation.lastMessage.status} />}
                        {previewText(conversation)}
                      </span>
                      {waiting && <span className={styles.waiting}><span className="visually-hidden">Espera respuesta</span></span>}
                    </span>
                    <span className={styles.meta}>
                      <span className={styles.source}>
                        <span className="visually-hidden">{CHANNEL_LABEL[conversation.channel]} · </span>
                        {conversation.connectionName}
                      </span>
                      {thread === 'other' || thread === 'free'
                        ? <span className={styles.tag} data-kind={thread}>{assignmentText(conversation, user.id)}</span>
                        : thread === 'mine' && <span className="visually-hidden">{assignmentText(conversation, user.id)}</span>}
                      {conversation.state !== 'OPEN' && (
                        <span className={styles.tag} data-kind={conversation.state}>{STATE_LABEL[conversation.state]}</span>
                      )}
                      {conversation.identityStatus === 'PENDING_REVIEW' && (
                        <span className={styles.tag} data-kind="warning">Identidad en revisión</span>
                      )}
                    </span>
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {list.hasNextPage && (
        <button type="button" className={`btn btn-secondary ${styles.more}`} disabled={list.isFetchingNextPage}
          onClick={() => void list.fetchNextPage()}>
          {list.isFetchingNextPage ? 'Cargando…' : 'Cargar más conversaciones'}
        </button>
      )}
    </div>
  );
}
